import type { TeamAverageScores } from '../types/team';

export interface BarometerValues {
  potentialExpressed: number;
  teamMood: number;
  blockerIndex: number;
}

export function computeBarometers(averages: TeamAverageScores): BarometerValues {
  const charScores = averages.averageScores;
  const harmScores = averages.averageHarmonicScores;

  const potentialExpressed =
    charScores.length > 0
      ? Math.round(
          charScores.reduce((sum, s) => sum + s.normalized, 0) / charScores.length
        )
      : 0;

  const upperLevels = harmScores.filter(s => s.levelId >= 8);
  const weights: Record<number, number> = { 8: 1, 9: 1.2, 10: 1.4, 11: 1.7, 12: 2 };
  let weightedSum = 0;
  let weightTotal = 0;
  upperLevels.forEach(s => {
    const w = weights[s.levelId] || 1;
    weightedSum += s.normalized * w;
    weightTotal += w;
  });
  const teamMood = weightTotal > 0 ? Math.round(weightedSum / weightTotal) : 0;

  const lowerLevels = harmScores.filter(s => s.levelId <= 7);
  const blockerIndex =
    lowerLevels.length > 0
      ? Math.round(
          lowerLevels.reduce((sum, s) => sum + s.normalized, 0) / lowerLevels.length
        )
      : 0;

  return { potentialExpressed, teamMood, blockerIndex };
}

type ThresholdLabel = { label: string; color: string };

export function getPotentialLabel(value: number): ThresholdLabel {
  if (value <= 30) return { label: 'Untapped', color: 'text-chocolate-500' };
  if (value <= 55) return { label: 'Developing', color: 'text-warm-600' };
  if (value <= 75) return { label: 'Strong', color: 'text-sage-600' };
  return { label: 'Exceptional', color: 'text-sage-700' };
}

export function getMoodLabel(value: number): ThresholdLabel {
  if (value <= 30) return { label: 'Concerning', color: 'text-coral-600' };
  if (value <= 55) return { label: 'Neutral', color: 'text-chocolate-500' };
  if (value <= 75) return { label: 'Positive', color: 'text-warm-600' };
  return { label: 'Thriving', color: 'text-sage-600' };
}

export function getBlockerLabel(value: number): ThresholdLabel {
  if (value <= 20) return { label: 'Minimal Drag', color: 'text-sage-600' };
  if (value <= 40) return { label: 'Moderate Friction', color: 'text-warm-600' };
  if (value <= 60) return { label: 'Significant Resistance', color: 'text-coral-500' };
  return { label: 'Critical Obstruction', color: 'text-coral-700' };
}
